import { useState, useEffect } from "react";
import { useAdmin } from "@/contexts/AdminContext";
import { toast } from "sonner";
import { Plus, Save, Trash2, Upload, Users, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyMember = { name: "", role: "", category: "associate", education: "", bar_enrolment: "", practice_areas: [], bio: "", photo_url: "", order: 0, status: "published", location: "", linkedin_url: "", contact_email: "" };

export default function AdminTeam() {
  const { api, token } = useAdmin();
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMember);
  const [practiceInput, setPracticeInput] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/team").then(r => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(s => ({ ...s, photo_url: `${API}/files/${res.data.path}` }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const addPracticeArea = () => {
    if (practiceInput.trim()) {
      setForm(s => ({ ...s, practice_areas: [...s.practice_areas, practiceInput.trim()] }));
      setPracticeInput("");
    }
  };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/team", form);
        toast.success("Team member created");
      } else {
        await api.put(`/admin/team/${editing}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      setForm(emptyMember);
      load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    try { await api.delete(`/admin/team/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-2xl text-[#0A1628] font-medium">Team Members</h1>
        <button onClick={() => { setEditing("new"); setForm(emptyMember); }} data-testid="add-team-member" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]">
          <Plus size={14} /> Add Member
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 p-6 rounded-[2px] mb-6" data-testid="team-form">
          <h3 className="font-heading text-lg text-[#0A1628] mb-4">{editing === "new" ? "New Team Member" : "Edit Team Member"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Name</label><input type="text" value={form.name} onChange={e => setForm(s => ({...s, name: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Role</label><input type="text" value={form.role} onChange={e => setForm(s => ({...s, role: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Category</label>
              <select value={form.category} onChange={e => setForm(s => ({...s, category: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]">
                <option value="founding">Founding Partner</option><option value="associate">Associate</option>
              </select>
            </div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(s => ({...s, status: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]">
                <option value="published">Published</option><option value="draft">Draft</option>
              </select>
            </div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Education</label><input type="text" value={form.education} onChange={e => setForm(s => ({...s, education: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Bar Enrolment</label><input type="text" value={form.bar_enrolment} onChange={e => setForm(s => ({...s, bar_enrolment: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Location</label><input type="text" value={form.location || ""} onChange={e => setForm(s => ({...s, location: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">LinkedIn URL</label><input type="text" value={form.linkedin_url || ""} onChange={e => setForm(s => ({...s, linkedin_url: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Contact Email</label><input type="text" value={form.contact_email || ""} onChange={e => setForm(s => ({...s, contact_email: e.target.value}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
            <div><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Order</label><input type="number" value={form.order} onChange={e => setForm(s => ({...s, order: parseInt(e.target.value) || 0}))} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
          </div>
          <div className="mt-4"><label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Bio</label><textarea value={form.bio} onChange={e => setForm(s => ({...s, bio: e.target.value}))} rows={4} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-[2px]" /></div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Practice Areas</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.practice_areas.map((a, i) => (
                <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-[2px] flex items-center gap-1">{a} <button onClick={() => setForm(s => ({...s, practice_areas: s.practice_areas.filter((_, j) => j !== i)}))} className="text-red-400 hover:text-red-600">&times;</button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={practiceInput} onChange={e => setPracticeInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addPracticeArea())} placeholder="Add area" className="flex-1 border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
              <button onClick={addPracticeArea} type="button" className="text-xs bg-slate-100 px-3 py-2 rounded-[2px] hover:bg-slate-200">Add</button>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">Profile Photo</label>
            <div className="flex items-center gap-4">
              {form.photo_url && <img src={form.photo_url} alt="Preview" className="w-16 h-16 object-cover rounded-[2px] border" />}
              <label className="flex items-center gap-2 text-xs border border-slate-200 px-4 py-2 rounded-[2px] cursor-pointer hover:border-[#0A1628]">
                <Upload size={14} /> {uploading ? "Uploading..." : "Upload Photo"}
                <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
              </label>
              <span className="text-xs text-slate-400">or</span>
              <input type="text" value={form.photo_url} onChange={e => setForm(s => ({...s, photo_url: e.target.value}))} placeholder="Paste URL" className="flex-1 border border-slate-200 px-3 py-2 text-sm rounded-[2px]" />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} data-testid="save-team-member" className="flex items-center gap-2 bg-[#0A1628] text-white text-xs px-5 py-2.5 uppercase tracking-wider hover:bg-[#1a2c4e] rounded-[2px]"><Save size={14} /> Save</button>
            <button onClick={() => setEditing(null)} className="text-xs px-5 py-2.5 border border-slate-200 text-slate-500 uppercase tracking-wider rounded-[2px]">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {members.map(m => (
          <div key={m.id} className="bg-white border border-slate-200 p-4 rounded-[2px] flex items-center justify-between hover:border-[#0A1628] transition-colors">
            <div className="flex items-center gap-4">
              {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-12 h-12 object-cover rounded-[2px]" /> : <div className="w-12 h-12 bg-slate-100 rounded-[2px] flex items-center justify-center text-slate-300"><Users size={20} /></div>}
              <div>
                <p className="text-[#0A1628] font-medium text-sm">{m.name}</p>
                <p className="text-slate-400 text-xs">{m.role}</p>
                <div className="flex gap-2 mt-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-[2px] ${m.status === "published" ? "bg-green-50 text-green-600" : "bg-yellow-50 text-yellow-600"}`}>{m.status}</span>
                  <span className="text-[10px] bg-slate-50 text-slate-500 px-2 py-0.5 rounded-[2px]">{m.category}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(m.id); setForm(m); setPracticeInput(""); }} className="text-xs text-slate-500 hover:text-[#0A1628] border border-slate-200 px-3 py-1.5 rounded-[2px]">Edit</button>
              <button onClick={() => remove(m.id)} className="text-xs text-red-400 hover:text-red-600 border border-slate-200 px-3 py-1.5 rounded-[2px]"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
